import {
  shapes as backstageShapes,
  PageTheme,
  createUnifiedTheme,
  palettes,
  createBaseThemeOptions,
} from "@backstage/theme";

// Devleaps brand colors — sourced from design system tokens
const devleapsColors = {
  primary: "#111110", // --ink-900: primary foreground / near-black
  secondary: "#5C5A55", // --ink-500: secondary / muted
  accent: "#FF4648", // --brand-red: interactive accent
  background: "#FAFAF7", // --ink-50: page background
  surface: "#F1F0EB", // --ink-100: elevated surfaces
  textPrimary: "#111110", // --fg
  textSecondary: "#5C5A55", // --fg-muted
  error: "#B22122", // --brand-red-ink
  warning: "#C77800", // --warn
  success: "#1F8A4C", // --ok
  info: "#111110", // use ink for info — no blue in palette
  sidebar: "#0A0A09", // --ink-950: dark sidebar
  bursts: {
    fontColor: "#FAFAF7",
    slackChannelText: "#E4E2DA",
    backgroundColor: {
      default: "#111110",
    },
  },
};

// Create custom page themes
const pageTheme: Record<string, PageTheme> = {
  home: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  documentation: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave2,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  tool: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.round,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  forge: {
    colors: [devleapsColors.accent, "#B22122"],
    shape: backstageShapes.round,
    backgroundImage: `linear-gradient(${devleapsColors.accent}, #B22122)`,
    fontColor: "#FAFAF7",
  },
  service: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  website: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  library: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  other: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  app: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
  apis: {
    colors: [devleapsColors.primary, devleapsColors.sidebar],
    shape: backstageShapes.wave,
    backgroundImage: `linear-gradient(-137deg, ${devleapsColors.primary} 0%, ${devleapsColors.sidebar} 100%)`,
    fontColor: "#FAFAF7",
  },
};

// Use createUnifiedTheme to build the BackstageTheme object
const backstageTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: devleapsColors.accent,
      },
      secondary: {
        main: devleapsColors.secondary,
      },
      error: {
        main: devleapsColors.error,
      },
      warning: {
        main: devleapsColors.warning,
      },
      info: {
        main: devleapsColors.info,
      },
      success: {
        main: devleapsColors.success,
      },
      background: {
        default: devleapsColors.background,
        paper: devleapsColors.surface,
      },
      text: {
        primary: devleapsColors.textPrimary,
        secondary: devleapsColors.textSecondary,
      },
      navigation: {
        background: devleapsColors.sidebar,
        indicator: devleapsColors.accent,
        color: "#E4E2DA",
        selectedColor: "#FAFAF7",
      },
    },
  }),
  defaultPageTheme: "home",
  pageTheme,
  components: {
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: "none",
          backgroundColor: devleapsColors.background,
          boxShadow: "0 1px 0 0 #E4E2DA", // --shadow-1
          paddingBottom: 8,
          borderBottom: "1px solid #E4E2DA",
        },
        title: {
          color: devleapsColors.textPrimary,
          fontWeight: 700,
          fontSize: "1.75rem",
          marginRight: 0,
          display: "inline-block",
          verticalAlign: "middle",
          "&::after": {
            content: '"|"',
            marginLeft: 16,
            marginRight: 16,
            color: devleapsColors.textSecondary,
            fontWeight: 300,
            opacity: 0.5,
          },
        },
        subtitle: {
          color: devleapsColors.textSecondary,
          marginLeft: 0,
          fontSize: "1rem",
          fontWeight: 400,
          display: "inline-flex",
          alignItems: "center",
          verticalAlign: "middle",
          lineHeight: "1.75rem",
        },
        type: {
          display: "none",
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        root: {
          color: devleapsColors.textSecondary,
        },
        label: {
          color: devleapsColors.textPrimary,
        },
        value: {
          color: devleapsColors.accent,
        },
      },
    },
  },
});

// For compatibility, if some older components still need a raw Material-UI theme,
// you can get it from the BackstageTheme.
// However, UnifiedThemeProvider should primarily use `backstageTheme`.

export { backstageTheme as baseTheme };
